'use client';

import { useEffect } from 'react';
import type { CSSProperties } from 'react';
import { reportClientBoundaryError } from '@/lib/report-client-boundary-error';
import { E } from '@/lib/editorial';

const btnRow: CSSProperties = {
  borderRadius: 0,
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  cursor: 'pointer',
  fontFamily: E.sans,
  fontSize: 11,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  padding: '16px 32px',
};

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error('Global error boundary caught an error:', error);

    if (process.env.NODE_ENV === 'production') {
      console.error('Production global error:', {
        message: error.message,
        stack: error.stack,
        digest: error.digest,
        timestamp: new Date().toISOString(),
      });
      reportClientBoundaryError(error, 'global');
    }
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          background: E.cream,
          color: E.ink,
          fontFamily: E.sans,
        }}
      >
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '48px 24px',
            boxSizing: 'border-box',
          }}
        >
          <div style={{ width: '100%', maxWidth: 720 }}>
            <div style={{ ...E.label, marginBottom: 12 }}>Error · Critical</div>

            <p
              style={{
                fontFamily: E.serif,
                fontSize: 'clamp(64px, 14vw, 120px)',
                fontWeight: 400,
                fontStyle: 'italic',
                lineHeight: 0.9,
                margin: '0 0 8px',
                color: E.goldDeep,
                opacity: 0.45,
                letterSpacing: '-0.02em',
              }}
            >
              !
            </p>

            <h1
              role="alert"
              style={{
                fontFamily: E.serif,
                fontSize: 'clamp(32px, 5vw, 48px)',
                fontWeight: 400,
                fontStyle: 'italic',
                margin: '0 0 16px',
                lineHeight: 1.05,
                color: E.ink,
              }}
            >
              Something went wrong.
            </h1>

            <p style={{ ...E.body, color: E.muted, maxWidth: 520, marginBottom: error.digest ? 10 : 28 }}>
              A critical error occurred while loading the application. Try again, or return to the home page.
            </p>

            {error.digest ? (
              <p
                style={{
                  ...E.cap,
                  color: E.muted,
                  opacity: 0.85,
                  marginBottom: 28,
                }}
              >
                Reference: {error.digest}
              </p>
            ) : null}

            {process.env.NODE_ENV === 'development' && (
              <details
                style={{
                  ...E.body,
                  fontSize: 13,
                  color: E.inkSoft,
                  marginBottom: 28,
                  maxWidth: 520,
                }}
              >
                <summary
                  style={{
                    cursor: 'pointer',
                    color: E.goldDeep,
                    fontFamily: E.sans,
                    fontSize: 10,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                  }}
                >
                  Error details (development only)
                </summary>
                <pre
                  style={{
                    marginTop: 12,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    padding: 16,
                    background: E.cream2,
                    border: `1px solid ${E.ruleSoft}`,
                    fontSize: 11,
                    lineHeight: 1.5,
                    fontFamily: 'ui-monospace, monospace',
                    color: E.ink,
                  }}
                >
                  {error.message}
                  {error.stack ? `\n\n${error.stack}` : ''}
                </pre>
              </details>
            )}

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                paddingTop: 8,
                borderTop: `1px solid ${E.rule}`,
                alignItems: 'stretch',
              }}
            >
              <button type="button" onClick={reset} style={{ ...E.btn, ...btnRow }}>
                Try again
              </button>
              <a href="/" style={{ ...E.btnGhost, ...btnRow }}>
                Return home
              </a>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
